import { useMutation } from "@tanstack/react-query";
import { useCartContext } from "app/providers/CartProvider";
import axios from "axios";
import { zakelijkDoorCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "shared/api";
import { STORAGE_ORDER_KEY, STORAGE_PAYMENT_URL } from "shared/config";
import { decryptVoucher } from "shared/utils/voucher";
import {
  CheckoutTabIds,
  useCheckoutTabsContext,
} from "pages/checkout/providers/CheckoutTabsProvider";

export const useCheckoutRequest = () => {
  const cartContext = useCartContext();
  const cart = cartContext.getCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateCurrentTab } = useCheckoutTabsContext();

  const canCheckout = () => {
    if (
      cart.products.length > 0 ||
      cart.supplyProducts.length > 0 ||
      cart.standardProducts.length > 0
    ) {
      return true;
    }
    return false;
  };

  const checkout = async () => {
    if (!canCheckout()) {
      toast.error("Er is iets foutgegaan bij het plaatsen van uw order");
      return;
    }

    const newCart = { ...cart };

    // Decrypt the discount code back to original string
    newCart.discountCode = decryptVoucher(cart.discountCode);

    if (
      newCart.products.some((product) =>
        zakelijkDoorCodes.includes(product.doorType.code),
      )
    ) {
      newCart.isSelfAssembly = false;
    }

    if (newCart.isSelfAssembly === true) {
      newCart.bumbalFlow = "shipment";
    }

    // Add labels to products without labels
    if (newCart.isSelfAssembly) {
      newCart.products = newCart.products.map((product, index) => {
        if (product.isSelfAssembly && !product.label) {
          product.label = "Zelfmontage " + (index + 1);
        }
        return product;
      });
    }

    const res = await api().checkout(newCart, { method: "POST" });
    return res.data;
  };

  const {
    mutate: mutateCheckout,
    isLoading: isCheckoutRequestLoading,
    isError: isCheckoutRequestFailed,
    data: checkoutRequestData,
  } = useMutation(checkout, {
    onSuccess: (data) => {
      window.scrollTo(0, 0);
      if (!data) {
        toast.error("Er is iets foutgegaan bij het plaatsen van uw order");
        return;
      }

      const { nextStep, paymentUrl, isSampleSale, orderId } = data;

      localStorage.setItem(STORAGE_ORDER_KEY, orderId);
      localStorage.setItem(STORAGE_PAYMENT_URL, paymentUrl);

      if (nextStep === "appointment") {
        searchParams.set("reference", data.orderId);
        searchParams.set(
          "type",
          isSampleSale || cart.isSelfAssembly ? "shipment" : "measurement",
        );
        setSearchParams(searchParams);

        // Go to the appointment tab (Appointment)
        updateCurrentTab(CheckoutTabIds.APPOINTMENT);
      } else if (nextStep === "redirect" && paymentUrl) {
        window.location.href = paymentUrl;
      }
    },
    onError: (e: any) => {
      if (
        axios.isAxiosError(e) &&
        e.response?.data.includes(
          "Cannot convert sample sale cart to order: 400",
        )
      ) {
        toast.error("Deze sample sale deur is helaas al verkocht!");
      } else {
        toast.error("Er is iets foutgegaan bij het plaatsen van uw order");
      }
    },
  });

  return {
    mutateCheckout,
    isCheckoutRequestLoading,
    isCheckoutRequestFailed,
    checkoutRequestData,
  };
};
